import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ReportReason } from '../api'
import { Modal } from './modal'

const REPORT_REASONS: ReportReason[] = [
  'spam',
  'harassment',
  'inappropriate_content',
  'unsafe_behavior',
  'scam',
  'other',
]

interface ReportDialogProps {
  name: string
  busy: boolean
  errorKey: string | null
  onSubmit: (reason: ReportReason, description: string) => void
  onClose: () => void
}

/**
 * Six-reason user report dialog (D-043). The target type/id are supplied by the
 * public-profile screen; the dialog only owns the reason and optional details.
 */
export function ReportDialog({
  name,
  busy,
  errorKey,
  onSubmit,
  onClose,
}: ReportDialogProps) {
  const { t } = useTranslation()
  const reasonId = useId()
  const descriptionId = useId()
  const [reason, setReason] = useState<ReportReason | ''>('')
  const [description, setDescription] = useState('')
  const [reasonError, setReasonError] = useState(false)

  function submit() {
    if (!reason) {
      setReasonError(true)
      return
    }
    setReasonError(false)
    onSubmit(reason, description.trim())
  }

  return (
    <Modal
      open
      title={t('profileSocial.report.title', { name })}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="btn-secondary min-h-11 px-4 text-sm"
            disabled={busy}
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn-primary min-h-11 px-4 text-sm"
            disabled={busy}
            aria-busy={busy}
            onClick={submit}
          >
            {busy
              ? t('profileSocial.report.submitting')
              : t('profileSocial.report.submit')}
          </button>
        </div>
      }
    >
      <p className="text-sm leading-relaxed text-muted">
        {t('profileSocial.report.body', { name })}
      </p>
      <div className="mt-4">
        <label htmlFor={reasonId} className="field-label">
          {t('profileSocial.report.reasonLabel')}
        </label>
        <select
          id={reasonId}
          className="field-input"
          value={reason}
          aria-invalid={reasonError || undefined}
          aria-describedby={reasonError ? `${reasonId}-error` : undefined}
          onChange={(event) => {
            setReason(event.target.value as ReportReason | '')
            setReasonError(false)
          }}
        >
          <option value="">
            {t('profileSocial.report.reasonPlaceholder')}
          </option>
          {REPORT_REASONS.map((value) => (
            <option key={value} value={value}>
              {t(`profileSocial.report.reasons.${value}`)}
            </option>
          ))}
        </select>
        {reasonError ? (
          <p
            id={`${reasonId}-error`}
            role="alert"
            className="mt-1 text-sm font-semibold text-danger"
          >
            {t('profileSocial.report.reasonRequired')}
          </p>
        ) : null}
      </div>
      <div className="mt-4">
        <label htmlFor={descriptionId} className="field-label">
          {t('profileSocial.report.description')}
        </label>
        <textarea
          id={descriptionId}
          className="field-input min-h-24"
          rows={3}
          maxLength={2000}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      {errorKey ? (
        <p role="alert" className="mt-3 text-sm font-semibold text-danger">
          {t(errorKey)}
        </p>
      ) : null}
    </Modal>
  )
}
